import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { AREAS, findArea } from "@/lib/areas";
import { LIVEIN_FAQS } from "@/lib/faq";
import PageHero from "@/components/PageHero";
import EnquiryForm from "@/components/EnquiryForm";
import CTABanner from "@/components/CTABanner";
import FAQSection from "@/components/FAQSection";

// Inject a LocalBusiness JSON-LD per-town to power "live-in care near me" rich results.
const injectLocalBusinessSchema = (area) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `SeaShells Care — ${area.name}`,
    description: area.intro,
    areaServed: [area.name, ...(area.nearby || [])],
    telephone: "+441992892894",
    email: "info@seashellcare.com",
    url: `https://seashellcare.com/live-in-care/${area.slug}`,
    address: {
      "@type": "PostalAddress",
      addressLocality: area.name,
      addressRegion: "Essex",
      postalCode: area.postcode,
      addressCountry: "GB",
    },
    priceRange: "££££",
  };
  const tag = document.createElement("script");
  tag.type = "application/ld+json";
  tag.setAttribute("data-local-schema", area.slug);
  tag.textContent = JSON.stringify(schema);
  document.head.appendChild(tag);
  return tag;
};

const AreaPage = () => {
  const { slug } = useParams();
  const area = findArea(slug);

  // Inject local-business schema for SEO
  useEffect(() => {
    if (!area) return;
    document.title = `Live-In Care in ${area.name} | SeaShells Care · Essex`;
    const tag = injectLocalBusinessSchema(area);
    return () => {
      document.head.removeChild(tag);
    };
  }, [area]);

  if (!area) return <Navigate to="/live-in-care" replace />;

  return (
    <main data-testid={`page-area-${area.slug}`}>
      <PageHero
        eyebrow={`${area.name} · ${area.postcode}`}
        title={area.headline}
        subtitle={area.intro}
      />

      {/* Why us in this town */}
      <section className="section">
        <div className="container-px max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <span className="eyebrow">Local to {area.name}</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-[var(--ss-teal-800)]">
              In-home care for elders, by people who actually live here.
            </h2>

            <ul className="mt-8 space-y-4">
              {area.why.map((line, i) => (
                <li key={i} className="flex gap-3" data-testid={`area-why-${i + 1}`}>
                  <span className="mt-1 w-6 h-6 rounded-full bg-[var(--ss-gold)]/20 text-[var(--ss-gold-dark)] flex items-center justify-center text-xs shrink-0">
                    <i className="fa-solid fa-check"></i>
                  </span>
                  <span className="text-[var(--ss-ink)] leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 rounded-2xl bg-[var(--ss-cream)] border border-[var(--ss-gold)]/30 p-6">
              <div className="font-serif text-2xl text-[var(--ss-teal-800)]">
                Also covering nearby
              </div>
              <p className="text-sm text-[var(--ss-ink-soft)] mt-2">
                We provide live-in carers for elderly residents in the
                surrounding villages too:
              </p>
              <ul className="mt-4 flex flex-wrap gap-2">
                {(area.nearby || []).map((n) => (
                  <li
                    key={n}
                    className="px-3 py-1.5 rounded-full bg-white border border-[var(--ss-line)] text-sm text-[var(--ss-teal-800)]"
                  >
                    {n}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10">
              <Link to="/live-in-care" className="btn btn-outline-dark" data-testid="area-livein-link">
                What is live-in care?
                <i className="fa-solid fa-arrow-right"></i>
              </Link>
            </div>
          </div>

          {/* Sticky enquiry */}
          <aside className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="bg-[var(--ss-cream)] rounded-3xl p-7 border border-[var(--ss-gold)]/30">
              <div className="font-serif text-2xl text-[var(--ss-teal-800)]">
                Live-in care in {area.name}
              </div>
              <p className="mt-2 text-sm text-[var(--ss-ink-soft)]">
                Free home assessment from a senior member of our team — usually
                within a working day.
              </p>
              <div className="mt-5">
                <EnquiryForm defaultService="live-in" sourcePage={`area-${area.slug}`} />
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection
        eyebrow="Common questions"
        title={`Live-in care in ${area.name} — frequently asked.`}
        items={LIVEIN_FAQS.slice(0, 6)}
        testIdPrefix={`area-${area.slug}-faq`}
        variant="cream"
      />

      <CTABanner
        eyebrow={`${area.name} families`}
        heading="Let's talk about your parent."
        body="Free, no-pressure home assessment in your loved one's living room. Bring the questions — we'll bring the experience."
      />
    </main>
  );
};

export default AreaPage;
